import { evidenceItems, evidenceTypes, shortNotices } from "@/mocks/evidence";
import type { EvidenceItem } from "@/mocks/evidence";

type EvidenceFlowProps = {
  query: string;
  onQueryChange: (value: string) => void;
  activeType: string;
  onTypeChange: (value: string) => void;
};

const badgeStyle: Record<string, string> = {
  chat: "bg-secondary-100 text-secondary-900",
  together: "bg-primary-100 text-primary-700",
  location: "bg-accent-100 text-accent-900",
  timestamp: "bg-background-200 text-foreground-800",
};

const noticeStyle: Record<string, string> = {
  更新: "bg-primary-100 text-primary-700",
  核对: "bg-secondary-100 text-secondary-900",
  上调: "bg-accent-100 text-accent-900",
  存疑: "bg-primary-100 text-primary-700",
  待补: "bg-background-200 text-foreground-700",
  确认: "bg-secondary-100 text-secondary-900",
};

export default function EvidenceFlow({
  query,
  onQueryChange,
  activeType,
  onTypeChange,
}: EvidenceFlowProps) {
  const keyword = query.trim().toLowerCase();
  const filtered: EvidenceItem[] = evidenceItems.filter((item) => {
    const matchType = activeType === "all" || item.type === activeType;
    const matchKeyword =
      !keyword ||
      item.event.toLowerCase().includes(keyword) ||
      item.summary.toLowerCase().includes(keyword) ||
      item.typeLabel.toLowerCase().includes(keyword) ||
      item.tags.some((tag) => tag.toLowerCase().includes(keyword));
    return matchType && matchKeyword;
  });

  const tabs = [{ id: "all", name: "全部" }, ...evidenceTypes.map((t) => ({ id: t.id, name: t.name }))];

  return (
    <div className="w-full px-4 md:px-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
        <div className="lg:col-span-2">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="font-label text-xs uppercase tracking-[0.22em] text-primary-600">
                  Evidence stream
                </span>
                <h3 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  实锤大图卡片流
                </h3>
              </div>
              <span className="font-label text-xs text-foreground-500">
                共 {filtered.length} 组匹配
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const active = activeType === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => onTypeChange(tab.id)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      active
                        ? "bg-foreground-950 text-background-50"
                        : "border border-background-300 bg-background-100/70 text-foreground-700 hover:border-secondary-300"
                    }`}
                  >
                    {tab.name}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5">
                <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={query}
                  onChange={(event) => onQueryChange(event.target.value)}
                  placeholder="检索事件名、标签或来源…"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="清空检索"
                    onClick={() => onQueryChange("")}
                    className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-primary-500"
                  >
                    <i className="ri-close-circle-line text-base" />
                  </button>
                )}
              </div>
              <span className="flex items-center gap-2 whitespace-nowrap font-label text-xs text-foreground-500">
                <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                  <i className="ri-shield-check-line text-sm" />
                </span>
                已标注来源与时间戳
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {filtered.length === 0 && (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-file-search-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600">
                  没有匹配到「{query || "当前筛选"}」的图组，换个事件名试试。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onQueryChange("");
                    onTypeChange("all");
                  }}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
                >
                  重置筛选
                </button>
              </div>
            )}

            {filtered.map((item) => (
              <article
                key={item.id}
                className={`group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 ${
                  item.featured ? "sm:grid-cols-2" : "sm:grid-cols-5"
                }`}
              >
                <div
                  className={`relative overflow-hidden ${
                    item.featured ? "h-56 sm:h-full" : "h-48 sm:col-span-2 sm:h-full"
                  }`}
                >
                  <img
                    src={item.cover}
                    alt={`黑料照片 实锤 ${item.event} ${item.typeLabel} 素材`}
                    title={`${item.event} · ${item.typeLabel}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span
                    className={`absolute left-3 top-3 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
                      badgeStyle[item.type] ?? "bg-background-200 text-foreground-800"
                    }`}
                  >
                    {item.typeLabel}
                  </span>
                </div>

                <div
                  className={`flex flex-col p-4 md:p-5 ${
                    item.featured ? "" : "sm:col-span-3"
                  }`}
                >
                  <h4 className="font-heading text-lg font-bold text-foreground-950">
                    {item.event}
                  </h4>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{item.summary}</p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <dl className="mt-4 grid grid-cols-1 gap-2 border-t border-background-200 pt-4 text-xs text-foreground-500 sm:grid-cols-3">
                    <div className="flex items-center gap-1.5">
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-price-tag-3-line text-sm" />
                      </span>
                      <span className="truncate">{item.source}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-time-line text-sm" />
                      </span>
                      <span className="truncate">{item.timestamp}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-map-pin-line text-sm" />
                      </span>
                      <span className="truncate">{item.location}</span>
                    </div>
                  </dl>
                </div>
              </article>
            ))}
          </div>
        </div>

        <aside className="flex flex-col gap-6 lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-base font-bold text-foreground-950">短讯更新</h4>
              <span className="flex items-center gap-1 font-label text-[11px] text-primary-600">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-flashlight-line text-sm" />
                </span>
                实时
              </span>
            </div>
            <ul className="mt-4 flex flex-col divide-y divide-background-200">
              {shortNotices.map((notice) => (
                <li key={notice.id} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="flex flex-col items-center pt-1">
                    <span className="font-label text-[11px] text-foreground-400">{notice.time}</span>
                  </span>
                  <div className="flex-1">
                    <p className="text-sm leading-6 text-foreground-800">{notice.title}</p>
                    <span
                      className={`mt-1.5 inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ${
                        noticeStyle[notice.tag] ?? "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {notice.tag}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                <i className="ri-list-check-2 text-lg" />
              </span>
              <h4 className="font-heading text-base font-bold text-secondary-900">可信度分档</h4>
            </div>
            <ul className="mt-4 flex flex-col gap-3 text-sm text-secondary-900">
              <li className="flex items-start gap-2">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center">
                  <i className="ri-checkbox-blank-circle-fill text-[8px] text-primary-500" />
                </span>
                高：来源明确 + 时间戳一致 + 定位可交叉
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center">
                  <i className="ri-checkbox-blank-circle-fill text-[8px] text-accent-500" />
                </span>
                中：来源可查，但缺少定位或同批次印证
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center">
                  <i className="ri-checkbox-blank-circle-fill text-[8px] text-foreground-400" />
                </span>
                待核：来源匿名或时间轴存在跳档
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}